import { addDays, subWeeks } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Download, Plus } from 'lucide-react'

import { VoucherCreateGeneralModal } from './VoucherCreateGeneralModal'
import { VoucherCreateOneToOneModal } from './VoucherCreateOneToOneModal'
import { VoucherHistoryTable } from './VoucherHistoryTable'
import { VoucherTable } from './VoucherTable'
import Loader from '../../../common/components/Loader'
import { Currency, Vouchers } from '../../../common/models'
import { IColumn } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getCurrencies,
  getSettingsTickets,
} from '../../../core/services/festival-settings'
import {
  VoucherPaginationParams,
  exportVouchersHistory,
  getVouchers,
  vouchersHistory,
} from '../../../core/services/voucher'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  DateTimePicker,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'

export const VouchersPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
    extraParams,
    setExtraParams,
  } = useTableParams({
    extraParams: {
      key: 'ga',
    },
  })

  const { hasEditPermission } = useCheckPermission({ page: 'VOUCHERS' })

  const { editionId, festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [showCreateVoucherModal, setShowCreateVoucherModal] =
    useState<boolean>(false)
  const [showCreateVoucherModalOTO, setShowCreateVoucherModalOTO] =
    useState<boolean>(false)
  const [startDate, setStartDate] = useState<number>(
    subWeeks(new Date(), 1).setHours(0, 0, 0).valueOf(),
  )
  const [endDate, setEndDate] = useState<number>(
    addDays(new Date(), 1).setHours(23, 59, 59).valueOf(),
  )
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentVoucher, setCurrentVoucher] = useState<Vouchers>()
  const [generalVouchers, setGeneralVouchers] = useState<Vouchers[]>([])
  const [otoVouchers, setOtoVouchers] = useState<Vouchers[]>([])
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [key, setKey] = useState<string>(extraParams.key || 'ga')
  const [loading, setLoading] = useState<boolean>(false)
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [focused, setFocused] = useState(false)
  const [exporting, setExporting] = useState<boolean>(false)

  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<{ [key: string]: string | null }>({})

  const [dubaiIntegration, setDubaiIntegration] = useState<boolean>(false)

  useEffect(() => {
    if (startDate > endDate) {
      setEndDate(new Date(startDate).setHours(23, 59, 59))
    }
  }, [startDate])

  useEffect(() => {
    if (startDate > endDate) {
      setStartDate(new Date(endDate).setHours(0, 0, 0))
    }
  }, [endDate])

  const getCurrency = useCallback(async () => {
    try {
      const response = await getCurrencies()
      if (response.status === 200) {
        setCurrency(response.data.defaultCurrency)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [])

  const getSettings = useCallback(async () => {
    try {
      const { data, status } = await getSettingsTickets()
      if (status === 200) {
        setDubaiIntegration(data.dubaiIntegration)
      }
    } catch (err) {
      console.log(err)
    }
  }, [])

  const getData = useCallback(async () => {
    if (focused) {
      return
    }
    setLoading(true)
    try {
      const { data, status } =
        key === 'vh'
          ? await vouchersHistory({
              size,
              page,
              sortBy,
              startDate,
              endDate,
              sortDirection,
              voucherCode: searchParams.current.voucherCode,
            })
          : await getVouchers({
              size,
              page,
              sortBy,
              sortDirection,
              startDate,
              endDate,
              requestType: key === 'ga' ? 'GENERAL' : 'ONT_TO_ONE',
              voucherCode: searchParams.current.voucherCode,
            })
      if (status === 200) {
        if (key === 'ga') {
          setGeneralVouchers(data.content)
          setTotalElemenets(data.totalElements)
        } else {
          setOtoVouchers(data.content)

          setTotalElemenets(data.totalElements)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    size,
    page,
    sortBy,
    startDate,
    endDate,
    key,
    focused,
    editionId,
    festivalName,
    sortDirection,
  ])

  useEffect(() => {
    getData()
  }, [getData, editionId, festivalName])

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          const params: VoucherPaginationParams = {
            size,
            page: 0,
            sortBy,
            startDate,
            endDate,
            sortDirection,
          }

          params.voucherCode = searchParams.current.voucherCode

          const { data, status } =
            key === 'vh'
              ? await vouchersHistory({
                  ...params,
                })
              : await getVouchers({
                  ...params,
                  requestType: key === 'ga' ? 'GENERAL' : 'ONT_TO_ONE',
                })
          if (status === 200) {
            if (key === 'ga') {
              setGeneralVouchers(data.content)
              setTotalElemenets(data.totalElements)
            } else {
              setOtoVouchers(data.content)

              setTotalElemenets(data.totalElements)
            }
          }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size, sortBy, sortDirection],
  )

  useEffect(() => {
    getSettings()
  }, [getSettings, editionId, festivalName])

  useEffect(() => {
    getCurrency()
  }, [getCurrency, editionId, festivalName])

  const downloadCSV = (cssContent: string) => {
    if (!hasEditPermission) {
      return
    }
    const blob = new Blob([cssContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = 'vouchers.csv' // Change the filename if needed
    document.body.appendChild(a)
    a.click()

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 0)
  }

  const exportCsv = useCallback(async () => {
    if (!hasEditPermission) {
      return
    }
    setExporting(true)
    try {
      const { data, status } = await exportVouchersHistory({
        startDate,
        endDate,
      })
      if (status === 200) {
        downloadCSV(data)
      }

      // TODO: handle response
    } catch (err) {
      if (err instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(err.message)
      } else {
        console.error(err)
      }
    } finally {
      setExporting(false)
    }
  }, [startDate, endDate])

  const dateFilters = (
    <div className="grid grid-cols-2 gap-5">
      <DateTimePicker
        label="From"
        mode="date"
        value={new Date(startDate)}
        onChange={(date) => {
          if (date) {
            const newDate = new Date(date)
            newDate.setHours(0, 0, 0, 0)
            setStartDate(newDate.valueOf())
          }
          setFocused(false)
        }}
        placeholder="Select start date"
        labelClassName="flex flex-row items-center gap-2"
      />
      <DateTimePicker
        label="To"
        mode="date"
        labelClassName="flex flex-row items-center gap-2"
        value={new Date(endDate)}
        onChange={(date) => {
          if (date) {
            const newDate = new Date(date)
            newDate.setHours(23, 59, 59, 999)
            setEndDate(newDate.valueOf())
          }
          setFocused(false)
        }}
        placeholder="Select end date"
      />
    </div>
  )

  return (
    <>
      <Header
        pageTitle="Rewards"
        actions={
          <>
            {hasEditPermission && key === 'vh' && (
              <Button
                variant="secondary"
                onClick={exportCsv}
                disabled={exporting}
                className="border border-monochrome-120"
              >
                <Download className="w-4 h-4 text-white mr-2" />
                Export CSV
              </Button>
            )}

            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setEditMode(false)
                  setCurrentVoucher(undefined)
                  if (key === 'ga') {
                    setShowCreateVoucherModal(true)
                  } else {
                    setShowCreateVoucherModalOTO(true)
                  }
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Tabs
        value={key}
        onValueChange={(newKey) => {
          setKey(newKey)
          setExtraParams((s) => ({ ...s, key: newKey }))
          setPage(1)
        }}
        className="w-full"
      >
        <TabsList className="grid w-full mb-6">
          <div className="bg-white/10 p-2.5 rounded-[20px]">
            <TabsTrigger value="ga">General Vouchers / Discounts</TabsTrigger>
            <TabsTrigger value="oto">One to One</TabsTrigger>
            <TabsTrigger value="vh">Vouchers History</TabsTrigger>
          </div>
        </TabsList>
        <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100svh-200px)] overflow-y-auto">
          <div className="flex items-center gap-4 justify-between">
            <PageTitle
              title="Rewards"
              subtitle={
                key === 'ga'
                  ? 'General Vouchers / Discounts'
                  : key === 'oto'
                  ? 'One to One'
                  : 'Vouchers History'
              }
              className="mb-5"
            />
            {dateFilters}
          </div>

          <TabsContent value="ga" className="mt-0">
            <VoucherTable
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              setEditMode={setEditMode}
              setCurrentVoucher={setCurrentVoucher}
              vouchers={generalVouchers}
              setVouchers={setGeneralVouchers}
              isCheck={isCheck}
              handleSearchParams={handleSearchParams}
              setIsCheck={setIsCheck}
              page={page + 1}
              setPage={setPage}
              size={size}
              setSize={setSize}
              sortBy={sortBy}
              setSortBy={setSortBy}
              totalElements={totalElements}
              setTotalElemenets={setTotalElemenets}
              isOneToOne={false}
              setShowCreateVoucherModal={setShowCreateVoucherModal}
              currency={currency}
            />
          </TabsContent>

          <TabsContent value="oto" className="mt-0">
            <VoucherTable
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              setEditMode={setEditMode}
              vouchers={otoVouchers}
              setVouchers={setOtoVouchers}
              isCheck={isCheck}
              setIsCheck={setIsCheck}
              handleSearchParams={handleSearchParams}
              page={page + 1}
              setPage={setPage}
              size={size}
              setSize={setSize}
              sortBy={sortBy}
              setSortBy={setSortBy}
              totalElements={totalElements}
              setTotalElemenets={setTotalElemenets}
              isOneToOne={true}
              setShowCreateVoucherModal={setShowCreateVoucherModalOTO}
              setCurrentVoucher={setCurrentVoucher}
              currency={currency}
            />
          </TabsContent>

          <TabsContent value="vh" className="mt-0">
            <VoucherHistoryTable
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              vouchers={otoVouchers}
              setVouchers={setOtoVouchers}
              isCheck={isCheck}
              setIsCheck={setIsCheck}
              page={page + 1}
              handleSearchParams={handleSearchParams}
              setPage={setPage}
              size={size}
              setSize={setSize}
              sortBy={sortBy}
              setSortBy={setSortBy}
              totalElements={totalElements}
              setTotalElemenets={setTotalElemenets}
              isOneToOne={true}
              currency={currency}
            />
          </TabsContent>
        </Card>
      </Tabs>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={setPage}
      />
      <VoucherCreateGeneralModal
        voucher={currentVoucher}
        editMode={editMode}
        setEditMode={setEditMode}
        setShowCreateModal={setShowCreateVoucherModal}
        showCreateModal={showCreateVoucherModal}
        onVoucherCreated={getData}
        dubaiIntegration={dubaiIntegration}
        currency={currency?.symbol || 'RON'}
      />
      <VoucherCreateOneToOneModal
        voucher={currentVoucher}
        editMode={editMode}
        setEditMode={setEditMode}
        setShowCreateModal={setShowCreateVoucherModalOTO}
        showCreateModal={showCreateVoucherModalOTO}
        onVoucherCreated={getData}
        dubaiIntegration={dubaiIntegration}
        currency={currency?.symbol || 'RON'}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
